import { CONVERSATION_MESSAGE_ITEM } from '@/config/api/api-routes';
import { ERROR_MESSAGE } from '@/config/messages';
import { http } from '@/shared/api/http';
import type { ApiErrorResponse } from '@/shared/types/result-api';
import { confirmMes } from '@/shared/utilities';
import { useMutation } from '@tanstack/react-query';
import i18next from 'i18next';
import { toast } from 'react-toastify';
import type { MessageDeleteReq } from '../models/message-action-dto';

interface MessageDeleteParams {
  messageId: string;
  conversationId: string;
  bucketId: string;
  scope?: 'Me' | 'Everyone';
}

export const useMessageDelete = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({ messageId, conversationId, bucketId, scope = 'Me' }: MessageDeleteParams) => {
      const requestBody: MessageDeleteReq = {
        bucketId,
        scope
      };

      await http.delete(
        CONVERSATION_MESSAGE_ITEM.replace(':conversationId', conversationId).replace(':messageId', messageId),
        {
          data: requestBody
        }
      );

      return { messageId, conversationId, bucketId, scope };
    },
    onSuccess: ({ scope }) => {
      toast.success(
        scope === 'Everyone' ? i18next.t('messages.toasts.recallSuccess') : i18next.t('messages.toasts.deleteSuccess')
      );
    },
    onError: (error: any) => {
      let errorMessage = ERROR_MESSAGE;

      // Handle different error response formats
      if (error?.response?.data) {
        const errorData = error.response.data as ApiErrorResponse;
        errorMessage = errorData.detail || errorData.message || errorData.title || ERROR_MESSAGE;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    }
  });

  return {
    xoa: async ({ messageId, conversationId, bucketId, scope = 'Me' }: MessageDeleteParams) => {
      const scopeText =
        scope === 'Everyone' ? i18next.t('messages.recall.scopeForAll') : i18next.t('messages.delete.scopeForMe');
      const res = await confirmMes({
        title:
          scope === 'Everyone' ? i18next.t('messages.recall.dialogTitle') : i18next.t('messages.delete.dialogTitle'),
        description: i18next.t('messages.delete.description', { scope: scopeText }),
        confirmText:
          scope === 'Everyone' ? i18next.t('messages.recall.confirmButton') : i18next.t('messages.delete.confirmButton')
      });

      if (res) {
        await mutateAsync({ messageId, conversationId, bucketId, scope });
      }
    },
    isDeleting: isPending
  };
};
